import React from 'react';

/**
 * The thin bars on the course page — a chapter's progress, a lesson's two
 * performance columns, and an exam's result.
 *
 * Not `Progress`, which is the continue-learning card's: that one is 12px with
 * a white track, a hairline and a pill-shaped fill inset by a pixel. These are
 * the "Course pages" frames' own — a flat gray/300 track and a flat fill,
 * 8px on a 4px radius, or 6px on 3px under an exam result.
 *
 * The fill starts at the inline start: the right-hand end in Arabic, as most of
 * the file draws it. (A few of the frames anchor it at the left; the file is
 * not consistent, and a bar that fills from a different end on the next card
 * reads as a different quantity.)
 *
 * `span`s, not `div`s, because the chapter header puts one inside a <button>.
 * With no `label` the bar is decorative — the percentage beside it is the text
 * a screen reader gets — and is hidden from assistive technology.
 */
const TONES = {
    brand: 'tw-bg-v2-brand',
    progress: 'tw-bg-v2-student-progress',
    done: 'tw-bg-v2-student-done',
    fail: 'tw-bg-v2-no',
    // red/400 — a chapter below the pass line in the results report. A step
    // brighter than `fail`, and the report's frame keeps the two apart.
    alert: 'tw-bg-v2-student-dot',
};

const TrackBar = ({ value, tone = 'brand', thin = false, label, className = '' }) => {
    const percent = Math.min(100, Math.max(0, Math.round(Number(value) || 0)));

    const a11y = label
        ? { role: 'progressbar', 'aria-valuenow': percent, 'aria-valuemin': 0, 'aria-valuemax': 100, 'aria-label': label }
        : { 'aria-hidden': true };

    return (
        <span
            {...a11y}
            className={
                'tw-relative tw-block tw-shrink-0 tw-overflow-hidden tw-bg-v2-ground-400 ' +
                `${thin ? 'tw-h-[6px] tw-rounded-[3px]' : 'tw-h-[8px] tw-rounded-[4px]'} ${className}`
            }
        >
            <span
                className={
                    'tw-absolute tw-inset-y-0 tw-start-0 tw-block tw-transition-[width] tw-duration-500 ' +
                    `${TONES[tone] ?? TONES.brand} ${thin ? 'tw-rounded-[3px]' : ''}`
                }
                style={{ width: `${percent}%` }}
            />
        </span>
    );
};

export default TrackBar;
